import type { ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "@v2/components/ui/button";

export type AlertBarTone = "info" | "warning";

const TONE_CLASSES: Record<AlertBarTone, string> = {
  info: "bg-raised text-foreground",
  warning: "bg-[hsl(var(--warn-soft))] text-foreground",
};

// CTA piena, colore coerente col tono: niente bg-card (in dark mode diventa
// grigio fango sopra la barra chiara).
const ACTION_CLASSES: Record<AlertBarTone, string> = {
  info: "bg-primary text-primary-foreground hover:bg-primary/90",
  warning: "bg-[hsl(var(--warn))] text-background hover:bg-[hsl(var(--warn))]/90",
};

const DISMISS_CLASSES: Record<AlertBarTone, string> = {
  info: "text-muted-foreground hover:bg-[hsl(var(--active))]",
  warning: "text-muted-foreground hover:bg-black/20",
};

/**
 * Barra di notifica in cima all'app (stile "alert bar"): icona + messaggio +
 * azione opzionale + dismiss. Componente puramente presentazionale; la logica
 * su quali barre mostrare e la persistenza del dismiss stanno in AppAlertBars.
 */
export function AlertBar({
  tone = "info",
  icon,
  children,
  actionLabel,
  onAction,
  onDismiss,
}: {
  tone?: AlertBarTone;
  icon?: ReactNode;
  children: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
}) {
  return (
    <div
      className={"flex items-center gap-3 rounded-2xl px-4 py-3 text-sm " + TONE_CLASSES[tone]}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <p className="flex-1 min-w-0 leading-snug">{children}</p>
      {actionLabel && onAction && (
        <Button
          size="sm"
          className={"h-9 shrink-0 " + ACTION_CLASSES[tone]}
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      )}
      {onDismiss && (
        <button
          type="button"
          aria-label="dismiss"
          onClick={onDismiss}
          className={
            "shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors " +
            DISMISS_CLASSES[tone]
          }
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
